/**
 * Class to handle the interactions with the Adobe Analytics API.
 */
export default class KoreAnalyticsHandler {
    constructor() {
        this.pageName = undefined;
        this.chatPageName = 'pub: global: chatbot';
        this.hasOpenedChat = false;
        this.hasSentMessage = false;
        this.hasClosedChat = false;
        this.rolAskUsHasBeenClicked = false;
    }

    /**
     * Set the custom props to send through the Adobe page setting interaction.
     * @param {String} pageName The page name of the interaction.
     */
    addChannelVars(pageName) {
        if (pageName && pageName.length) {

            const parts = pageName.toLowerCase().replace(/\s/g, '').split(':');
            if (parts.length > 0) {
                s.channel = parts[0];
            }

            if (parts.length > 1) {
                s.prop1 = `${s.channel}: ${parts[1]}`;
            }
            else {
                s.prop1 = s.channel;
            }

            if (parts.length > 2) {
                s.prop2 = `${s.prop1}: ${parts[2]}`;
            }
            else {
                s.prop2 = s.prop1;
            }

            if (parts.length > 3) {
                s.prop3 = `${s.prop2}: ${parts[3]}`;
            }
            else {
                s.prop3 = s.prop2;
            }

            if (parts.length > 4) {
                s.prop3 = `${s.prop3}: ${parts[4]}`;
            }
            else {
                s.prop4 = s.prop3;
            }
        }
    }

    /**
     * 
     */
    askUsClicked() {
        if (!this.isAdobeAvailable() || this.rolAskUsHasBeenClicked) {
            return;
        }
        this.prepAdobeCode();
        this.sendClickEvent(`ask-us-chatbot`, this.pageName);
        this.resetAdobeCode();
        this.rolAskUsHasBeenClicked = true;
    }

    /**
     * If the adobe global variable is present, then set the user sent message
     * event and trigger the synthetic click.
     */
    chatMessageSent() {
        if (!this.isAdobeAvailable() || this.hasSentMessage) {
            return;
        }
        this.prepAdobeCode('event51', true);
        this.sendClickEvent('send message to chatbot');
        this.resetAdobeCode();
        this.hasSentMessage = true;
    }

    /**
     * If the adobe global variable is present, then send the synthetic
     * click event for chat closed.
     */
    chatWindowClosed() {
        if (!this.isAdobeAvailable() || this.hasClosedChat) {
            return;
        }
        this.prepAdobeCode();
        this.sendClickEvent('exit chatbot');
        this.resetAdobeCode();
        this.hasClosedChat = true;
        this.hasOpenedChat = false;
        this.hasSentMessage = false;
        this.rolAskUsHasBeenClicked = false;
    }

    /**
     * If the adobe global variable is present, then set the user opened chat
     * event and trigger the synthetic click.
     */
    chatWindowOpened() {
        if (!this.isAdobeAvailable() || this.hasOpenedChat) {
            return;
        }
        this.prepAdobeCode('event50,event996');
        window.s.pageName = this.chatPageName;
        this.addChannelVars('pub: global: chatbot');
        window.s.t();
        this.resetAdobeCode();
        this.hasOpenedChat = true;
        this.hasClosedChat = false;
    }

    /**
     * Checks and returns if the Adobe global 's' object has been loaded
     * onto the present page.
     */
    isAdobeAvailable() {
        const isAvailable = typeof window.s !== 'undefined';
        if (!isAvailable) {
            console.warn('No global analytics object present.');
        }
        else if (!this.pageName) {
            this.pageName= window.s.pageName;
        }
        else {
            // Do nothing.
        }

        return isAvailable;
    }

    /**
     * Sets the Adobe object and the events to send before the chat trigger interactions.
     * @param {String} events The events being sent with the next interaction.
     * @param {Boolean} isLinkTracking If the interaction is triggered by a link or synthetic click.
     */
    prepAdobeCode(events, isLinkTracking = false) {
        window.s.clearVars();
        window.s.usePlugins = false;
        this.setEvents(events, isLinkTracking);
    }

    /**
     * Resets the Adobe object after the chat trigger interactions.
     */
    resetAdobeCode() {
        window.s.usePlugins = true;
        window.s.clearVars();
    }

    /**
     * Sends the synthetic click event to the adobe API.
     * @param {String} linkName The link name to send with the page name.
     */
    sendClickEvent(linkName, pageName = '') {
        let qualifiedName = '';
        if (pageName && pageName.length) {
            qualifiedName = `${linkName}|${pageName}`;
        }
        else if (!this.pageName && !this.chatPageName) {
            qualifiedName = `${linkName}`;
        }
        else {
            qualifiedName = `${linkName}|${this.chatPageName}`;
            window.s.pageName = this.chatPageName;
        }
        window.s.tl(this, 'o', `${qualifiedName}`);
    }

    /**
     * Clears the existing Adobe vars and sets the plugin status based on if
     * this is called before or after an interaction with the Adobe API.
     * @param {Boolean} usePlugins Whether to disable or enable adobe plugins.
     */
    setAdobeCode(usePlugins = false) {
        s.clearVars();
        s.usePlugins = usePlugins;
    }

    /**
     * Sets the event on the global Adobe object. If it is a link tracking event,
     * then also set the linkTrackEvents value with the event.
     * @param {String} events The event to set before triggering the Adobe API.
     * @param {Boolean} isLinkTracking If the interaction is triggered by a link or synthetic click.
     */
    setEvents(events, isLinkTracking = false) {
        if (!events || !events.length === 0) {
            return;
        }

        const eventsCollection = events.split(events, ',');
        if (isLinkTracking) {
            window.s.linkTrackEvents = window.s.linkTrackEvents || '';
            for (const event of eventsCollection) {
                if (window.s.linkTrackEvents.indexOf(events) === -1) {
                    if (window.s.linkTrackEvents.length > 0) {
                        window.s.linkTrackEvents += ',';
                    }

                    window.s.linkTrackEvents += event;
                }
            }
        }
        window.s.events = window.s.events || '';
        if (window.s.events.length) {
            s.events += ',';
        }
        window.s.events += events;
    }
}